// @ts-nocheck
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import isMobile from "ismobilejs";
import React from "react";
import styled, { ThemeConsumer, ThemeProps, withTheme } from "styled-components";

/* Starter Custom Code - Start */
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps, StyledIcon, StyledTypography } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import constants from "@store/constants";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { css } from "styled-components";
/* Starter Custom Code - End */

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
    // Starter Custom Code
    isViewAll?: boolean;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{
            id: string;
            className: string;
            onClick: () => void /* Starter Custom Code */;
        }>;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        // Starter Custom Code
        useOnHover?: boolean;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme> & HasHistory; // Starter Custom Code

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

const MenuStyle = styled.ul<MenuStyleProps>`
    background: ${getColor("common.background")};
    z-index: ${getProp("theme.zIndex.menu")};
    box-shadow: ${getProp("theme.shadows.2")};
    width: ${getProp("width", 175)}px;
    list-style: none;
    position: absolute;
    margin: 0;
    padding: 0;
    display: none;
    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen: boolean; isOpen?: true } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    position: relative; /* stylelint-disable */
    ${({ isOpen }) =>
        isOpen &&
        `& > ${MenuStyle} {
                display: block;
            }
    `}
    ${({ canBeOpen }) =>
        canBeOpen &&
        `
    &:focus-within > ${MenuStyle}  /* stylelint-enable */ {
        display: block;
    }
    .trigger:focus + ${MenuStyle},
    &:hover > ${MenuStyle},
    &.focus-within > ${MenuStyle} {
        display: block;
    }`}
    ${injectCss}
`;

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick: () => void;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    button, /* Starter Custom Code */
    a {
        width: 100%;
        min-height: 42px;
        justify-content: space-between;
        &:hover {
            background: ${getColor("common.border")};
        }
    }
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 10px 15px 10px 20px;
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
    ${({ hasChildren }: { hasChildren?: boolean }) => hasChildren && "width: calc(100% - 24px);"}
`;

const trigger = React.createRef<HTMLElement>();

/* Starter Custom Code - Start */
const viewAllClickableProps: LinkPresentationProps = {
    ...mergeProps(linkGetActionLinkProps(ArrowForward, "right", "1.2em"), {
        css: css`
            padding: ${pxToRem(10, 8, 10, 20)};

            && {
                ${StyledTypography}, ${StyledIcon} {
                    color: ${getColor("text.linkAltHover")};
                }
            }

            &:hover {
                && {
                    ${StyledTypography}, ${StyledIcon} {
                        color: ${getColor("text.linkHover")};
                    }
                }
            }
        `,
    }),
};
/* Starter Custom Code - End */

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
    // Starter Custom Code
    subMenuOpenState: { [key: string]: { currentDepth: number; value: boolean } };
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 175,
    };

    state: MenuState = {
        canBeOpen: !!this.props.useOnHover, // Starter Custom Code
        isMobile: false,
        shouldRender: true,
        // Starter Custom Code
        subMenuOpenState: {},
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });

        /* Starter Custom Code - Start */
        if (!this.props.useOnHover) {
            document.addEventListener("click", this.handleClickOutside);
        }
        /* Starter Custom Code - End */
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });

        /* Starter Custom Code - Start */
        if (!this.props.useOnHover) {
            document.removeEventListener("click", this.handleClickOutside);
        }
        /* Starter Custom Code - End */
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    /* Starter Custom Code - Start */
    closeMenuWithoutUrl = () => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    };

    handleTriggerClick = (hasChildren?: boolean) => {
        if (!this.props.useOnHover) {
            if (hasChildren) {
                this.setState(prevState => ({ canBeOpen: !prevState.canBeOpen }));
            } else {
                this.setState({ canBeOpen: false, subMenuOpenState: {} });
            }
        }
    };

    handleClickOutside = (event: MouseEvent) => {
        if (!this.menuWrapperRef.current?.contains(event.target as Node) && !this.props.useOnHover) {
            this.setState({ canBeOpen: false, subMenuOpenState: {} });
        }
    };

    toggleSubMenu = (key: string, currentDepth: number) => {
        if (this.state.subMenuOpenState[key]) {
            return;
        }
        this.setState(prev => {
            const filteredState: { [key: string]: { currentDepth: number; value: boolean } } = {};

            for (const [k, v] of Object.entries(prev.subMenuOpenState)) {
                if (v.currentDepth < currentDepth) {
                    filteredState[k] = v;
                }
            }

            const isOpen = prev.subMenuOpenState[key]?.value ?? false;
            filteredState[key] = {
                currentDepth,
                value: !isOpen,
            };

            return {
                subMenuOpenState: filteredState,
            };
        });
    };
    /* Starter Custom Code - End */

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    menuWrapperMouseLeaveHandler = () => {
        if (!this.menuWrapperRef.current) {
            return;
        }

        this.menuWrapperRef.current.blur();

        const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
        if (!menuTrigger) {
            return;
        }

        menuTrigger.blur();
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, mergeCss, ...otherProps } = this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
                    /* Starter Custom Code - Start */
                    const handleAction = (
                        item: MappedLink,
                        hasChildren: boolean | undefined,
                        itemKey: string,
                        currentDepth: number,
                    ) => {
                        if (!this.props.useOnHover) {
                            if (hasChildren) {
                                this.toggleSubMenu(itemKey, currentDepth);
                            } else {
                                this.handleTriggerClick(true);
                            }
                        } else {
                            item.url ? this.closeMenu() : this.closeMenuWithoutUrl();
                            this.closeMenuWithoutChildren(hasChildren);
                        }
                    };
                    /* Starter Custom Code - End */
                    const renderMenuItems = (childMenuItems: MappedLink[], currentDepth: number) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;
                        return (
                            <MenuStyle
                                width={otherProps.width}
                                css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                            >
                                {childMenuItems
                                    ?.filter(item => !item.excludeFromNavigation)
                                    .map((item, index) => {
                                        const hasChildren =
                                            item.children?.some(o => !o.excludeFromNavigation) &&
                                            currentDepth < maxDepth;

                                        /* Starter Custom Code - Start */
                                        const isFolderPage = item.type === constants.pageType.folder;
                                        const itemKey = `${item.title}_${index}`;
                                        const isSubMenuOpen = this.state.subMenuOpenState[itemKey] ?? false;
                                        const children =
                                            hasChildren && !this.props.useOnHover && !isFolderPage
                                                ? [
                                                      {
                                                          ...item,
                                                          children: undefined,
                                                          title: `${translate("View")} ${item.title}`,
                                                          isViewAll: true,
                                                      },
                                                      ...item.children!,
                                                  ]
                                                : item.children;
                                        /* Starter Custom Code - End */

                                        if (
                                            (item.type === "ChangeCustomerPage" &&
                                                !this.props.displayChangeCustomerLink) ||
                                            !this.state.shouldRender
                                        ) {
                                            return null;
                                        }

                                        return (
                                            <MenuItemStyle
                                                width={otherProps.width}
                                                css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                                key={itemKey} // Starter Custom Code
                                                data-test-selector="menuItem"
                                                // Starter Custom Code
                                                onClick={() => handleAction(item, hasChildren, itemKey, currentDepth)}
                                                onKeyDown={event => {
                                                    if (event.key === "Enter" || event.keyCode === 13) {
                                                        if (item.url && (this.props.useOnHover || !hasChildren)) {
                                                            this.props.history.push(item.url);
                                                        }
                                                        handleAction(item, hasChildren, itemKey, currentDepth);
                                                    }
                                                }}
                                            >
                                                {!this.props.useOnHover && item.isViewAll /* Starter Custom Code */ ? (
                                                    /* Starter Custom Code - Start */
                                                    <Link {...viewAllClickableProps} href={item.url}>
                                                        {item.title}
                                                    </Link>
                                                ) : (
                                                    /* Starter Custom Code - End */
                                                    <Clickable
                                                        href={
                                                            !hasChildren || this.props.useOnHover ? item.url : undefined
                                                        } // Starter Custom Code
                                                        // Starter Custom Code - move onClick to wrapper
                                                        target={item.openInNewWindow ? "_blank" : ""}
                                                        {...applyProp("menuItemClickableProps")}
                                                        {...item.clickableProps}
                                                        // Starter Custom Code
                                                        as={!hasChildren || this.props.useOnHover ? "a" : "button"}
                                                        aria-haspopup={hasChildren}
                                                        aria-expanded={
                                                            hasChildren &&
                                                            (this.props.useOnHover || isSubMenuOpen ? true : undefined)
                                                        }
                                                    >
                                                        <MenuItemText
                                                            hasChildren={hasChildren}
                                                            {...applyProp("menuItemTypographyProps")}
                                                            data-test-selector="menuItemText"
                                                        >
                                                            {item.title}
                                                        </MenuItemText>
                                                        {/* Starter Custom Code - Start */}
                                                        {item.iconProps && <IconMemo {...item.iconProps} />}
                                                        {/* Starter Custom Code - End */}
                                                        {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                                    </Clickable>
                                                )}
                                                {/* Starter Custom Code - Start */}
                                                {hasChildren &&
                                                    (this.props.useOnHover || isSubMenuOpen) &&
                                                    renderMenuItems(children!, currentDepth + 1)}
                                                {/* Starter Custom Code - End */}
                                            </MenuItemStyle>
                                        );
                                    })}
                            </MenuStyle>
                        );
                    };

                    const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            <MenuWrapper
                                ref={this.menuWrapperRef}
                                canBeOpen={this.state.canBeOpen}
                                isOpen={isOpen}
                                onMouseLeave={this.props.useOnHover ? this.menuWrapperMouseLeaveHandler : undefined} // Starter Custom Code
                                css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                {...{ "aria-labelledby": descriptionId }}
                                {...otherProps}
                            >
                                {React.cloneElement(menuTrigger, {
                                    id: descriptionId,
                                    className: "trigger",
                                    onClick: !this.props.useOnHover ? () => this.handleTriggerClick(true) : undefined, // Starter Custom Code
                                })}
                                {this.state.canBeOpen && renderMenuItems(menuItems, 1) /* Starter Custom Code */}
                            </MenuWrapper>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default withTheme(withHistory(Menu)); // Starter Custom Code

export { MenuItemText };
